import classNames from 'classnames';
import { AnimatePresence, m } from 'framer-motion';
import { useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import BookingFlowLoader from '@/components/organisms/booking-flow-loader/booking-flow-loader';
import Link from '@/components/utilities/link/link';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { usePageTransition } from '@/libraries/hooks/use-page-transition';
import styles from './in-page-navigation-search-details.module.scss';

export interface InPageNavigationSearchDetailsProps {
  /** The class name to apply */
  className?: string;
  /** The anchor link id to scroll to configuration flow module */
  configurationFlowAnchorLinkId?: string;
  /** The total price */
  totalPrice: number;
}

const fadeVariants = {
  exit: { opacity: 0, y: -10 },
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 },
};

const InPageNavigationSearchDetails = ({
  className,
  configurationFlowAnchorLinkId,
  totalPrice,
}: InPageNavigationSearchDetailsProps) => {
  const { t } = useI18n();
  const formattedTotalPrice = useCurrencyFormatter()(totalPrice);
  const {
    bookingUrl,
    setIsOpenConfigurationFlow,
    setCurrentStep,
    selectedRoom,
    stepSummaries,
  } = useHotelContext();
  const { predefinedSearchParameters } = usePredefinedSearchContext();
  const { ctaRefPosition, isVisibleInPageConfigurationFlow } =
    usePageDetailsContext();
  const [showBookingFlowLoader, setShowBookingFlowLoader] = useState(false);
  const isInPredefinedSearchPage = Boolean(predefinedSearchParameters);

  const showScrollToConfigurationFlow =
    !isVisibleInPageConfigurationFlow && ctaRefPosition === 'below';

  const showNextStep =
    (isVisibleInPageConfigurationFlow && ctaRefPosition === 'below') ||
    ctaRefPosition === 'above';

  const showSearchDetails =
    isVisibleInPageConfigurationFlow || ctaRefPosition === 'above';

  usePageTransition({
    onEnter: () => {
      setShowBookingFlowLoader(false);
    },
  });

  return (
    <div className={classNames(styles.component, className)}>
      <AnimatePresence initial={false}>
        {showSearchDetails && !isInPredefinedSearchPage && (
          <m.div
            key="search-details"
            animate="visible"
            className={styles.details}
            exit="exit"
            initial="hidden"
            transition={{ duration: 0.3 }}
            variants={fadeVariants}
          >
            <div className={styles.configurationDetails}>
              <Typography
                className={styles.chosenRooms}
                tagStyle="bodyMediumBold"
              >
                {t('items.common.words.chosenRooms')}
              </Typography>

              <Link
                className={styles.editSearchLink}
                url={`#${configurationFlowAnchorLinkId}`}
              >
                {t('items.common.words.edit')}
              </Link>
            </div>

            {selectedRoom}
          </m.div>
        )}
      </AnimatePresence>

      <div className={styles.buttonContainer}>
        <div className={styles.price}>
          <Typography className={styles.text} tagStyle="bodyMedium">
            {t('items.common.words.totalPrice')}
          </Typography>

          <Typography className={styles.text} tagStyle="headingMedium">
            {formattedTotalPrice}
          </Typography>
        </div>

        <AnimatePresence mode="wait">
          {showNextStep && stepSummaries.length > 1 ? (
            <m.div
              key="next-step"
              animate="visible"
              exit="exit"
              initial="hidden"
              transition={{ duration: 0.3 }}
              variants={fadeVariants}
            >
              <Button
                onClick={() => {
                  setIsOpenConfigurationFlow(true);
                  setCurrentStep(value => value + 1);
                }}
                text={t('items.common.words.next')}
                variant="primary"
              />
            </m.div>
          ) : showNextStep && bookingUrl ? (
            <m.div
              key="buy-now"
              animate="visible"
              exit="exit"
              initial="hidden"
              transition={{ duration: 0.3 }}
              variants={fadeVariants}
            >
              <Button
                className={styles.button}
                onClick={() => setShowBookingFlowLoader(true)}
                text={t('items.common.words.buyNow')}
                url={bookingUrl}
              />
            </m.div>
          ) : showScrollToConfigurationFlow && configurationFlowAnchorLinkId ? (
            <m.div
              key="edit-room-option"
              animate="visible"
              exit="exit"
              initial="hidden"
              transition={{ duration: 0.3 }}
              variants={fadeVariants}
            >
              <Button
                className={styles.button}
                text={t('items.searchWidget.editRoomOption')}
                url={`#${configurationFlowAnchorLinkId}`}
                variant="primary"
              />
            </m.div>
          ) : null}
        </AnimatePresence>
      </div>

      <BookingFlowLoader
        isOpen={showBookingFlowLoader}
        onClose={() => setShowBookingFlowLoader(false)}
      />
    </div>
  );
};

InPageNavigationSearchDetails.displayName = 'InPageNavigationSearchDetails';

export default InPageNavigationSearchDetails;
